import { Link } from 'react-router-dom';
import type { NewsItem } from '@/types/content';
import { formatDate, formatViews } from '@/utils/content';

interface HeadlineFocusProps {
  lead: NewsItem;
  side: NewsItem[];
}

export default function HeadlineFocus({ lead, side }: HeadlineFocusProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
      <Link
        to={`/news/${lead.id}`}
        className="lg:col-span-2 group relative block h-72 md:h-[420px] rounded-lg overflow-hidden border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
      >
        <img
          src={lead.cover}
          alt={lead.title}
          title={`${lead.title} - ${lead.categoryName}`}
          className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium mb-3">
            <i className="ri-flashlight-fill" />
            头条聚焦
          </span>
          <h3 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 group-hover:text-primary-400 transition-colors leading-snug">
            {lead.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2.5 line-clamp-2 leading-relaxed max-w-2xl">{lead.summary}</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
            <span>{lead.source}</span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {formatDate(lead.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(lead.views)}
            </span>
          </div>
        </div>
      </Link>

      <div className="flex flex-col gap-3">
        {side.map((item, i) => (
          <Link
            key={item.id}
            to={`/news/${item.id}`}
            className="group flex items-start gap-3 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
          >
            <span className="font-heading text-lg font-extrabold text-primary-500/70 w-6 shrink-0 text-center leading-6">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
                {item.title}
              </h4>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
                <span>{item.categoryName}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {formatViews(item.views)}
                </span>
              </div>
            </div>
            <div className="w-20 h-14 rounded-md overflow-hidden shrink-0 hidden sm:block">
              <img
                src={item.cover}
                alt={item.title}
                title={item.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}